import React, { useEffect, useMemo, useState } from "react";
import { getMyDirectoryProfile, upsertMyDirectoryProfile, deleteMyDirectoryProfile } from "../api/directory";

const defaults = {
  published: false,
  displayName: "",
  email: "",
  phoneMobile: "",
  phoneOther: "",
  addressStreet: "",
  addressPostalCode: "",
  addressCity: "",
  notes: "",
};

export default function MyDirectoryProfile() {
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getMyDirectoryProfile();
        if (!mounted) return;
        setForm(data ? { ...defaults, ...data } : defaults);
      } catch (e) {
        if (!mounted) return;
        setErr("Failed to load profile.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onToggle = (e) => setForm((f) => ({ ...f, published: e.target.checked }));

  const body = useMemo(() => ({
    published: !!form.published,
    displayName: form.displayName?.trim() || undefined,
    email: form.email?.trim() || undefined,
    phoneMobile: form.phoneMobile?.trim() || undefined,
    phoneOther: form.phoneOther?.trim() || undefined,
    addressStreet: form.addressStreet?.trim() || undefined,
    addressPostalCode: form.addressPostalCode?.trim() || undefined,
    addressCity: form.addressCity?.trim() || undefined,
    notes: form.notes?.trim() || undefined,
  }), [form]);

  const onSave = async () => {
    setSaving(true);
    setErr(null); setMsg(null);
    try {
      const saved = await upsertMyDirectoryProfile(body);
      setForm((f) => ({ ...f, ...saved }));
      setMsg("Saved.");
    } catch (e) {
      setErr("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete your directory profile?")) return;
    setErr(null); setMsg(null);
    try {
      await deleteMyDirectoryProfile();
      setForm(defaults);
      setMsg("Profile deleted.");
    } catch (e) {
      setErr("Failed to delete.");
    }
  };

  if (loading) return <div className="p-6">Loading…</div>;

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-2xl font-semibold mb-4">My Directory Profile</h1>

      <label className="flex items-center gap-2 mb-6">
        <input type="checkbox" checked={!!form.published} onChange={onToggle} />
        <span>Publish in the school directory</span>
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input name="displayName" value={form.displayName} onChange={onChange} placeholder="Display name" className="border rounded p-2" />
        <input name="email" value={form.email} onChange={onChange} placeholder="Email" className="border rounded p-2" />
        <input name="phoneMobile" value={form.phoneMobile} onChange={onChange} placeholder="Mobile phone" className="border rounded p-2" />
        <input name="phoneOther" value={form.phoneOther} onChange={onChange} placeholder="Other phone" className="border rounded p-2" />
        <input name="addressStreet" value={form.addressStreet} onChange={onChange} placeholder="Street" className="border rounded p-2 sm:col-span-2" />
        <input name="addressPostalCode" value={form.addressPostalCode} onChange={onChange} placeholder="ZIP/Postal code" className="border rounded p-2" />
        <input name="addressCity" value={form.addressCity} onChange={onChange} placeholder="City" className="border rounded p-2" />
        <textarea name="notes" value={form.notes} onChange={onChange} placeholder="Notes (optional)" className="border rounded p-2 sm:col-span-2" rows={3} />
      </div>

      <div className="flex gap-3 mt-6">
        <button className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-60" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button className="px-4 py-2 rounded border" onClick={() => setForm(defaults)} disabled={saving}>
          Reset
        </button>
        <button className="ml-auto px-4 py-2 rounded bg-red-600 text-white" onClick={onDelete}>
          Delete profile
        </button>
      </div>

      {(msg || err) && (
        <div className={`mt-4 p-3 rounded ${err ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>
          {err || msg}
        </div>
      )}
    </div>
  );
}